import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { AgentCardProps } from './AgentCard';

interface AddAssistantProps {
  onCancel: () => void;
  onSave: (assistant: AgentCardProps) => void;
  onReject?: (assistant: AgentCardProps) => void;
  onApprove?: (assistant: AgentCardProps) => void;
  initialData?: AgentCardProps;
  isReviewMode?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  page: {
    background: theme.palette.background.default,
    padding: '32px 24px 48px',
  },
  container: {
    width: '100%',
    maxWidth: 1000,
    margin: '0 auto',
  },
  pageHeader: {
    textAlign: 'center',
    marginBottom: 20,
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: 32,
    lineHeight: '40px',
    marginBottom: 8,
    color: theme.palette.text.secondary,
  },
  pageSubtitle: {
    color: theme.palette.text.tertiary,
    fontSize: 16,
    lineHeight: '24px',
  },
  sectionTitle: {
    marginTop: 26,
    marginBottom: 14,
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: 20,
    lineHeight: '28px',
  },
  subsectionTitle: {
    marginTop: 12,
    marginBottom: 8,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: 16,
    lineHeight: '24px',
  },
  helperText: {
    color: theme.palette.text.tertiary,
    fontSize: 13,
    lineHeight: '18px',
    marginTop: 4,
  },
  actionBar: {
    marginTop: 10,
    marginBottom: 24,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
  },
  actionButton: {
    textTransform: 'none',
    minWidth: 130,
    height: 40,
    borderRadius: 4,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '20px',
  },
  primaryBtn: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      background: theme.palette.primary.dark || theme.palette.primary.main,
    },
  },
  lightBtn: {
    background: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.border}`,
  },
  dangerBtn: {
    background: theme.palette.error.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      background: theme.palette.error.dark || theme.palette.error.main,
    },
  },
  sectionBlock: {
    marginBottom: 10,
  },
  requiredLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '20px',
    marginBottom: 10,
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  checkboxLabel: {
    '& .MuiTypography-root': {
      fontSize: 16,
      lineHeight: '24px',
      color: theme.palette.text.secondary,
    },
  },
  validationBox: {
    border: `1px solid ${theme.palette.success.main}`,
    background: theme.palette.successBackground,
    borderRadius: 8,
    padding: 16,
    marginTop: 16,
    marginBottom: 16,
  },
  validationTitle: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '24px',
    marginBottom: 8,
  },
  validationText: {
    fontSize: 14,
    lineHeight: '22px',
    color: theme.palette.text.secondary,
    marginBottom: 8,
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
  evidenceSection: {
    border: `1px solid ${theme.palette.border}`,
    borderRadius: 8,
    padding: 16,
    background: theme.palette.background.tertiary,
  },
  evidenceTitle: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '24px',
    marginBottom: 10,
    color: theme.palette.text.secondary,
  },
  fileInputWrap: {
    border: `1px dashed ${theme.palette.border}`,
    borderRadius: 4,
    padding: 10,
    marginTop: 8,
    background: theme.palette.background.paper,
  },
  dividerRow: {
    display: 'flex',
    alignItems: 'center',
    margin: '14px 0',
    gap: 10,
  },
  dividerLine: {
    flex: 1,
    borderBottom: `1px solid ${theme.palette.border}`,
  },
  dividerText: {
    color: theme.palette.text.tertiary,
    fontWeight: 600,
    fontSize: 13,
    lineHeight: '18px',
  },
  releaseNotesCard: {
    border: `1px solid ${theme.palette.border}`,
    borderRadius: 8,
    background: theme.palette.background.paper,
    padding: 16,
    marginBottom: 12,
  },
  addVersionBtn: {
    textTransform: 'none',
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '20px',
    height: 40,
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      background: theme.palette.background.paper,
    },
    '& .MuiInputBase-input': {
      fontSize: 14,
      lineHeight: '20px',
      color: theme.palette.text.secondary,
    },
    '& .MuiInputLabel-outlined': {
      fontSize: 14,
      lineHeight: '20px',
      color: theme.palette.text.tertiary,
    },
  },
}));

export const AddAssistant: React.FC<AddAssistantProps> = ({
  onCancel,
  onSave,
  onReject,
  onApprove,
  initialData,
  isReviewMode = false,
}) => {
  const classes = useStyles();
  const theme = useTheme<Theme>();

  const withCurrentOption = (options: string[], current?: string) => {
    if (!current || options.includes(current)) {
      return options;
    }
    return [current, ...options];
  };

  const normalizeAiCategoryForForm = (value?: string) =>
    value === 'Copilot'
      ? 'Co-pilot'
      : value === 'Deployed (CBRE Hosted)'
      ? 'Other'
      : value || '';

  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [ownerEmail] = useState(initialData?.ownerEmail || '');
  const [ownerName, setOwnerName] = useState(initialData?.owner || '');
  const [agentType, setAgentType] = useState(
    normalizeAiCategoryForForm(
      initialData?.aiCategory ||
        initialData?.modalContent?.targetAudience?.aiCategory ||
        initialData?.agentType,
    ),
  );
  const [segment, setSegment] = useState(
    initialData?.modalContent?.targetAudience?.segment || initialData?.segment || '',
  );
  const [designedFor, setDesignedFor] = useState(initialData?.modalContent?.targetAudience?.designedFor || '');
  const [businessGroup, setBusinessGroup] = useState(initialData?.modalContent?.targetAudience?.businessGroup || '');
  const [geography, setGeography] = useState(initialData?.modalContent?.targetAudience?.geography || 'Global');
  const [geographicDetails, setGeographicDetails] = useState(initialData?.modalContent?.targetAudience?.geographicDetails || '');

  const [integrationConfiguration, setIntegrationConfiguration] = useState(
    initialData?.modalContent?.integrationMethod?.configuration || '',
  );
  const [additionalSystems, setAdditionalSystems] = useState(
    initialData?.modalContent?.integrationMethod?.additionalSystems || '',
  );
  const [agentUrl, setAgentUrl] = useState(
    initialData?.modalContent?.agentConfiguration?.agentUrl || '',
  );
  const [agentVersion, setAgentVersion] = useState(
    initialData?.modalContent?.agentConfiguration?.agentVersion || '',
  );
  const [skills, setSkills] = useState<string[]>(
    initialData?.modalContent?.agentConfiguration?.skills || [],
  );
  const [systemCapabilities, setSystemCapabilities] = useState<string[]>(
    initialData?.modalContent?.agentConfiguration?.systemCapabilities || [],
  );
  const [inputFormat, setInputFormat] = useState(
    initialData?.modalContent?.interactionSettings?.inputFormat || '',
  );
  const [responseFormat, setResponseFormat] = useState(
    initialData?.modalContent?.interactionSettings?.responseFormat || '',
  );
  const [requiresSecureAccess, setRequiresSecureAccess] = useState(
    !!initialData?.modalContent?.interactionSettings?.requiresSecureAccess,
  );

  const [apiEndpoint, setApiEndpoint] = useState(
    initialData?.modalContent?.connectionDetails?.apiEndpoint || '',
  );
  const [authenticationMethod, setAuthenticationMethod] = useState(
    initialData?.modalContent?.connectionDetails?.authenticationMethod || '',
  );
  const [connectionType, setConnectionType] = useState(
    initialData?.modalContent?.connectionDetails?.connectionType || '',
  );
  const [maximumInputSize, setMaximumInputSize] = useState(
    initialData?.modalContent?.connectionDetails?.maximumInputSize || '',
  );
  const [responseTime, setResponseTime] = useState(
    initialData?.modalContent?.connectionDetails?.responseTime || '',
  );

  const defaultUseCases = [
    'Finance',
    'Leasing',
    'Research & Analytics',
    'Productivity',
    'Marketing',
    'Legal & Compliance',
  ];
  const [useCases, setUseCases] = useState<string[]>(
    initialData?.useCases?.map(item => item.name) || [],
  );
  const [customUseCase, setCustomUseCase] = useState('');

  const initialPairs = useMemo(() => {
    const provided = initialData?.modalContent?.whatYouProvide || [];
    const returned = initialData?.modalContent?.whatYouGet || [];
    const length = Math.max(provided.length, returned.length, 1);
    return Array.from({ length }, (_, index) => ({
      input: provided[index] || '',
      output: returned[index] || '',
    }));
  }, [initialData]);
  const [inputOutputPairs, setInputOutputPairs] = useState(initialPairs);
  const [efficiency, setEfficiency] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const segmentOptions = withCurrentOption(
    ['Global Advisory', 'BOE', 'REI', 'Corporate'],
    segment,
  );
  const assistantTypeOptions = withCurrentOption(
    ['Ellis AI', 'Co-pilot', 'Other'],
    agentType,
  );
  const geographyOptions = withCurrentOption(
    ['Global', 'North America', 'Europe', 'Asia Pacific', 'Latin America'],
    geography,
  );

  const efficiencyOptions = [
    'Time Savings',
    'Cost Reduction',
    'Improved Quality',
    'Productivity Gains',
  ];
  const inputFormatOptions = ['Text', 'File Upload', 'API Input'];
  const responseFormatOptions = [
    'JSON',
    'XML',
    'CSV',
    'Plain Text',
    'Structured Output',
    'HTML',
    'PDF',
    'Custom',
  ];
  const skillsOptions = [
    'Compliance Review',
    'Document Processing',
    'Data Analysis',
    'Task Automation',
    'Workflow Orchestration',
    'Knowledge Retrieval',
    'Summarization',
    'Content Generation',
  ];
  const systemCapabilitiesOptions = [
    'Multi-language Support',
    'Real-time Processing',
    'Batch Processing',
    'Caching',
    'Logging',
    'Monitoring',
    'Alerting',
    'Encryption',
    'Authentication',
    'Authorization',
    'Rate Limiting',
    'Load Balancing',
    'Failover',
    'Backup & Recovery',
    'Audit Trail',
    'API Documentation',
    'Error Handling',
    'Performance Optimization',
  ];

  const fieldDisabled = isReviewMode;
  const normalizedCategory = agentType.trim().toLowerCase();
  const isEllisCategory = normalizedCategory === 'ellis ai';
  const isCopilotCategory =
    normalizedCategory === 'co-pilot' || normalizedCategory === 'copilot';
  const isExternalCategory =
    normalizedCategory === 'other' ||
    normalizedCategory === 'deployed (cbre hosted)';
  const hasSelectedType = !!agentType.trim();

  const showIntegrationMethodSection = isEllisCategory || isCopilotCategory;
  const showAgentConfigurationSection = isCopilotCategory || isExternalCategory;
  const showInteractionSettingsSection = isCopilotCategory || isExternalCategory;
  const showConnectionDetailsSection = isExternalCategory;

  const handleUseCaseToggle = (option: string) => {
    if (fieldDisabled) {
      return;
    }
    setUseCases(prev =>
      prev.includes(option)
        ? prev.filter(item => item !== option)
        : [...prev, option],
    );
  };

  const handleAddCustomUseCase = () => {
    if (fieldDisabled) {
      return;
    }
    const normalized = customUseCase.trim();
    if (!normalized) {
      return;
    }
    if (useCases.includes(normalized)) {
      setCustomUseCase('');
      return;
    }
    setUseCases(prev => [...prev, normalized]);
    setCustomUseCase('');
  };

  const updatePair = (
    index: number,
    key: 'input' | 'output',
    value: string,
  ) => {
    setInputOutputPairs(prev =>
      prev.map((pair, pairIndex) =>
        pairIndex === index ? { ...pair, [key]: value } : pair,
      ),
    );
  };

  const addInputOutputPair = () => {
    if (fieldDisabled) {
      return;
    }
    setInputOutputPairs(prev => [...prev, { input: '', output: '' }]);
  };

  const buildAssistant = (status: 'Approved' | 'Under Review' | 'Rejected' | 'Resubmit'): AgentCardProps => {
    const today = new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
    });

    const whatYouProvide = inputOutputPairs
      .map(pair => pair.input.trim())
      .filter(Boolean);
    const whatYouGet = inputOutputPairs
      .map(pair => pair.output.trim())
      .filter(Boolean);

    const selectedAiCategory =
      agentType === 'Co-pilot'
        ? 'Copilot'
        : agentType === 'Other'
        ? 'Deployed (CBRE Hosted)'
        : agentType;
    const selectedAssistantType =
      initialData?.agentType === 'Personal Assistant' ||
      initialData?.agentType === 'Team Assistant' ||
      initialData?.agentType === 'Enterprise Assistant'
        ? initialData.agentType
        : 'Team Assistant';

    const useCaseColors: Record<string, string> = {
      Finance: '#059669',
      Leasing: '#0891B2',
      'Research & Analytics': '#7C3AED',
      Productivity: '#D97706',
      Marketing: '#DB2777',
      'Legal & Compliance': '#374151',
    };

    return {
      id: initialData?.id,
      title,
      description,
      avatarColor: initialData?.avatarColor || theme.palette.secondary.main,
      useCases: (useCases.length ? useCases : ['Productivity']).map(name => ({
        name,
        color: useCaseColors[name] || theme.palette.success.main,
      })),
      agentType: selectedAssistantType,
      aiCategory: selectedAiCategory || undefined,
      agentTypeColor: theme.palette.success.main,
      owner: ownerName || initialData?.owner || 'Unknown',
      ownerEmail: ownerEmail || undefined,
      approvalStatus: status,
      updatedTime: today,
      bookmarked: initialData?.bookmarked || false,
      modalContent: {
        overview: [description || title],
        capabilities: efficiency ? [`Efficiency: ${efficiency}`] : [],
        targetAudience: {
          designedFor: designedFor || 'CBRE teams',
          businessGroup: businessGroup || 'Enterprise',
          geography,
          geographicDetails: geographicDetails || undefined,
          segment: segment || undefined,
          aiCategory: selectedAiCategory || undefined,
        },
        whatYouProvide,
        whatYouGet,
        governance: {
          dataAccess: 'Internal only',
          compliance: 'Pending review',
          validationSummary: 'Validation summary pending',
          validatedBy: 'AI Governance',
          version: 'v1.0',
          importantToKnow:
            'Users are responsible for validating all outputs before use.',
        },
        integrationMethod: showIntegrationMethodSection
          ? {
              method: 'URL',
              configuration: integrationConfiguration || undefined,
              additionalSystems: additionalSystems || undefined,
            }
          : undefined,
        agentConfiguration: showAgentConfigurationSection
          ? {
              agentUrl: agentUrl || undefined,
              agentVersion: agentVersion || undefined,
              skills: skills.length ? skills : undefined,
              systemCapabilities: systemCapabilities.length
                ? systemCapabilities
                : undefined,
            }
          : undefined,
        interactionSettings: showInteractionSettingsSection
          ? {
              inputFormat: inputFormat || undefined,
              responseFormat: responseFormat || undefined,
              requiresSecureAccess,
            }
          : undefined,
        connectionDetails: showConnectionDetailsSection
          ? {
              apiEndpoint: apiEndpoint || undefined,
              authenticationMethod: authenticationMethod || undefined,
              connectionType: connectionType || undefined,
              maximumInputSize: maximumInputSize || undefined,
              responseTime: responseTime || undefined,
            }
          : undefined,
        releaseNotes: [],
      },
    };
  };

  const canSaveDraft =
    !!title.trim() && !!description.trim() && !!segment && !!agentType;
  const canSubmit =
    canSaveDraft && !!ownerName && acknowledged;

  const renderCreateTopActions = () => (
    <Box className={classes.actionBar}>
      <Button className={`${classes.actionButton} ${classes.lightBtn}`} variant="contained" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        className={`${classes.actionButton} ${classes.primaryBtn}`}
        variant="contained"
        disabled={!canSaveDraft}
        onClick={() => onSave(buildAssistant('Under Review'))}
      >
        Save
      </Button>
    </Box>
  );

  const renderCreateBottomActions = () => (
    <Box className={classes.actionBar}>
      <Button
        className={`${classes.actionButton} ${classes.lightBtn}`}
        variant="contained"
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button
        className={`${classes.actionButton} ${classes.primaryBtn}`}
        variant="contained"
        disabled={!canSubmit}
        onClick={() => onSave(buildAssistant('Under Review'))}
      >
        Submit
      </Button>
    </Box>
  );

  const renderReviewActions = () => (
    <Box className={classes.actionBar}>
      <Button className={`${classes.actionButton} ${classes.lightBtn}`} variant="contained" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        className={`${classes.actionButton} ${classes.dangerBtn}`}
        variant="contained"
        onClick={() => onReject?.(buildAssistant('Rejected'))}
      >
        Reject
      </Button>
      <Button
        className={`${classes.actionButton} ${classes.primaryBtn}`}
        variant="contained"
        onClick={() => onApprove ? onApprove(buildAssistant('Approved')) : onSave(buildAssistant('Approved'))}
      >
        Approve
      </Button>
    </Box>
  );

  return (
    <Box className={classes.page}>
      <Box className={classes.container}>
        <Box className={classes.pageHeader}>
          <Typography className={classes.pageTitle}>
            {isReviewMode
              ? 'Review Assistant'
              : initialData
              ? 'Edit Assistant'
              : 'Add New Assistant'}
          </Typography>
          <Typography className={classes.pageSubtitle}>
            {isReviewMode
              ? 'Review and approve or reject this assistant'
              : initialData
              ? 'Update this AI assistant configuration'
              : 'Create a new AI assistant to share with your team or organization'}
          </Typography>
        </Box>

        {isReviewMode ? renderReviewActions() : renderCreateTopActions()}

        <Typography className={classes.sectionTitle}>Basic Information</Typography>
        <Grid container spacing={2} className={classes.sectionBlock}>
          <Grid item xs={12}>
            <TextField
              label="Assistant Name *"
              fullWidth
              variant="outlined"
              size="small"
              className={classes.inputField}
              value={title}
              onChange={e => setTitle(e.target.value)}
              disabled={fieldDisabled}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description *"
              fullWidth
              variant="outlined"
              size="small"
              className={classes.inputField}
              value={description}
              onChange={e => setDescription(e.target.value)}
              disabled={fieldDisabled}
            />
            <Typography className={classes.helperText}>
              Note: Avatar initials and color will be automatically generated from the assistant name
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined" size="small" disabled={fieldDisabled}>
              <InputLabel>Segment *</InputLabel>
              <Select value={segment} onChange={e => setSegment(String(e.target.value))} label="Segment *">
                <MenuItem value="">Select segment...</MenuItem>
                {segmentOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Typography className={classes.sectionTitle}>Classification</Typography>
        <Grid container spacing={2} className={classes.sectionBlock}>
          <Grid item xs={12}>
            <Typography className={classes.requiredLabel}>Agent Type</Typography>
            <FormControl fullWidth variant="outlined" size="small" disabled={fieldDisabled}>
              <InputLabel>AI Category *</InputLabel>
              <Select
                value={agentType}
                onChange={e => {
                  const nextCategory = String(e.target.value);
                  setAgentType(nextCategory);
                  if (
                    nextCategory !== 'Ellis AI' &&
                    nextCategory !== 'Co-pilot'
                  ) {
                    setIntegrationConfiguration('');
                    setAdditionalSystems('');
                  }
                  if (nextCategory === 'Ellis AI') {
                    setAgentUrl('');
                    setAgentVersion('');
                    setSkills([]);
                    setSystemCapabilities([]);
                    setInputFormat('');
                    setResponseFormat('');
                    setRequiresSecureAccess(false);
                    setApiEndpoint('');
                    setAuthenticationMethod('');
                    setConnectionType('');
                    setMaximumInputSize('');
                    setResponseTime('');
                  }
                }}
                label="AI Category *"
              >
                <MenuItem value="">Select type...</MenuItem>
                {assistantTypeOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {showIntegrationMethodSection && (
            <>
              <Grid item xs={12}>
                <Typography className={classes.subsectionTitle}>Integration Method</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Integration Method"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  value="URL"
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="URL Configuration"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  value={integrationConfiguration}
                  onChange={e => setIntegrationConfiguration(e.target.value)}
                  disabled={fieldDisabled}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Are there any additional systems that you need access that the user of this agent might need access to?"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  value={additionalSystems}
                  onChange={e => setAdditionalSystems(e.target.value)}
                  disabled={fieldDisabled}
                  multiline
                  rows={3}
                />
              </Grid>
            </>
          )}

          {showAgentConfigurationSection && (
            <>
              <Grid item xs={12}>
                <Typography className={classes.subsectionTitle}>Agent Configuration</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Agent URL"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  value={agentUrl}
                  onChange={e => setAgentUrl(e.target.value)}
                  disabled={fieldDisabled}
                />
                <Typography className={classes.helperText}>
                  The endpoint URL for accessing this agent
                </Typography>
              </Grid>
              {isCopilotCategory && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label="Agent Version"
                      fullWidth
                      variant="outlined"
                      size="small"
                      className={classes.inputField}
                      value={agentVersion}
                      onChange={e => setAgentVersion(e.target.value)}
                      disabled={fieldDisabled}
                    />
                    <Typography className={classes.helperText}>
                      Current version of the agent
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined" size="small" disabled={fieldDisabled}>
                      <InputLabel>Skills</InputLabel>
                      <Select
                        multiple
                        value={skills}
                        onChange={e => setSkills(e.target.value as string[])}
                        label="Skills"
                      >
                        {skillsOptions.map(option => (
                          <MenuItem key={option} value={option}>
                            <Checkbox checked={skills.includes(option)} color="primary" />
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography className={classes.helperText}>
                      Select one or more skills this agent can perform
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined" size="small" disabled={fieldDisabled}>
                      <InputLabel>System Capabilities</InputLabel>
                      <Select
                        multiple
                        value={systemCapabilities}
                        onChange={e => setSystemCapabilities(e.target.value as string[])}
                        label="System Capabilities"
                      >
                        {systemCapabilitiesOptions.map(option => (
                          <MenuItem key={option} value={option}>
                            <Checkbox checked={systemCapabilities.includes(option)} color="primary" />
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography className={classes.helperText}>
                      Select one or more system capabilities available for this agent
                    </Typography>
                  </Grid>
                </>
              )}
            </>
          )}

          {showInteractionSettingsSection && (
            <>
              <Grid item xs={12}>
                <Typography className={classes.subsectionTitle}>Interaction Settings</Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small" disabled={fieldDisabled}>
                  <InputLabel>Input Format</InputLabel>
                  <Select
                    value={inputFormat}
                    onChange={e => setInputFormat(String(e.target.value))}
                    label="Input Format"
                  >
                    <MenuItem value="">Select input format...</MenuItem>
                    {inputFormatOptions.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography className={classes.helperText}>
                  Select the format in which the agent receives input
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small" disabled={fieldDisabled}>
                  <InputLabel>Response Format</InputLabel>
                  <Select
                    value={responseFormat}
                    onChange={e => setResponseFormat(String(e.target.value))}
                    label="Response Format"
                  >
                    <MenuItem value="">Select response format...</MenuItem>
                    {responseFormatOptions.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography className={classes.helperText}>
                  Specify the expected output format and structure
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  className={classes.checkboxLabel}
                  control={
                    <Switch
                      checked={requiresSecureAccess}
                      onChange={e => setRequiresSecureAccess(e.target.checked)}
                      disabled={fieldDisabled}
                      color="primary"
                    />
                  }
                  label="Requires Secure Access"
                />
                <Typography className={classes.helperText}>
                  Enable if the assistant needs authentication or restricted access
                </Typography>
              </Grid>
            </>
          )}
        </Grid>

        {hasSelectedType && (
          <>
            <Typography className={classes.sectionTitle}>Use Cases</Typography>
            <Typography className={classes.helperText}>
              Select predefined use cases or add custom ones
            </Typography>
            <Grid container spacing={2} className={classes.sectionBlock}>
              <Grid item xs={12}>
                <Box className={classes.checkboxGroup}>
                  {defaultUseCases.map(option => (
                    <FormControlLabel
                      key={option}
                      className={classes.checkboxLabel}
                      control={
                        <Checkbox
                          checked={useCases.includes(option)}
                          onChange={() => handleUseCaseToggle(option)}
                          disabled={fieldDisabled}
                          color="primary"
                        />
                      }
                      label={option}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  label="Add Custom Use Case"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  value={customUseCase}
                  onChange={e => setCustomUseCase(e.target.value)}
                  disabled={fieldDisabled}
                />
              </Grid>
              <Grid item xs={2}>
                <Button
                  className={`${classes.actionButton} ${classes.lightBtn}`}
                  variant="contained"
                  fullWidth
                  onClick={handleAddCustomUseCase}
                  disabled={fieldDisabled || !customUseCase.trim()}
                >
                  Add
                </Button>
              </Grid>
            </Grid>

            <Typography className={classes.sectionTitle}>Owner Details</Typography>
            <Grid container spacing={2} className={classes.sectionBlock}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Owner/Team Name *"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  value={ownerName}
                  onChange={e => setOwnerName(e.target.value)}
                  disabled={fieldDisabled}
                />
              </Grid>
            </Grid>
          </>
        )}

        {showConnectionDetailsSection && (
          <>
            <Typography className={classes.sectionTitle}>Connection Details</Typography>
            <Grid container spacing={2} className={classes.sectionBlock}>
              <Grid item xs={12}>
                <TextField
                  label="API Endpoint"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  value={apiEndpoint}
                  onChange={e => setApiEndpoint(e.target.value)}
                  disabled={fieldDisabled}
                />
                <Typography className={classes.helperText}>
                  The API endpoint URL for connecting to this assistant
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Authentication Method"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  value={authenticationMethod}
                  onChange={e => setAuthenticationMethod(e.target.value)}
                  disabled={fieldDisabled}
                />
                <Typography className={classes.helperText}>
                  Specify the authentication method required for API access
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Connection Type"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  value={connectionType}
                  onChange={e => setConnectionType(e.target.value)}
                  disabled={fieldDisabled}
                />
                <Typography className={classes.helperText}>
                  The protocol or type of connection supported
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Maximum Input Size"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  value={maximumInputSize}
                  onChange={e => setMaximumInputSize(e.target.value)}
                  disabled={fieldDisabled}
                />
                <Typography className={classes.helperText}>
                  Maximum allowed input size per request
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Response Time"
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.inputField}
                  value={responseTime}
                  onChange={e => setResponseTime(e.target.value)}
                  disabled={fieldDisabled}
                />
                <Typography className={classes.helperText}>
                  Expected response time for API calls
                </Typography>
              </Grid>
            </Grid>
          </>
        )}

        <Typography className={classes.sectionTitle}>Target Audience</Typography>
        <Grid container spacing={2} className={classes.sectionBlock}>
          <Grid item xs={12}>
            <TextField
              label="Designed For"
              fullWidth
              variant="outlined"
              size="small"
              className={classes.inputField}
              value={designedFor}
              onChange={e => setDesignedFor(e.target.value)}
              disabled={fieldDisabled}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Business Group"
              fullWidth
              variant="outlined"
              size="small"
              className={classes.inputField}
              value={businessGroup}
              onChange={e => setBusinessGroup(e.target.value)}
              disabled={fieldDisabled}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth variant="outlined" size="small" disabled={fieldDisabled}>
              <InputLabel>Geography</InputLabel>
              <Select value={geography} onChange={e => setGeography(String(e.target.value))} label="Geography">
                <MenuItem value="">Select geography...</MenuItem>
                {geographyOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Specific Location (Optional)"
              fullWidth
              variant="outlined"
              size="small"
              className={classes.inputField}
              value={geographicDetails}
              onChange={e => setGeographicDetails(e.target.value)}
              disabled={fieldDisabled || !geography}
            />
          </Grid>
        </Grid>

        <Typography className={classes.sectionTitle}>Input & Output</Typography>
        <Typography className={classes.helperText}>
          Specify the inputs and outputs for this agent
        </Typography>
        <Grid container spacing={2} className={classes.sectionBlock}>
          {inputOutputPairs.map((pair, index) => (
            <React.Fragment key={`pair-${index}`}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Describe what you send or upload to this agent"
                fullWidth
                variant="outlined"
                size="small"
                className={classes.inputField}
                value={pair.input}
                onChange={e => updatePair(index, 'input', e.target.value)}
                disabled={fieldDisabled}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Describe what this agent produces or returns"
                fullWidth
                variant="outlined"
                size="small"
                className={classes.inputField}
                value={pair.output}
                onChange={e => updatePair(index, 'output', e.target.value)}
                disabled={fieldDisabled}
              />
            </Grid>
            </React.Fragment>
          ))}
        </Grid>
        {!fieldDisabled && (
          <Button
            className={`${classes.actionButton} ${classes.lightBtn}`}
            variant="contained"
            onClick={addInputOutputPair}
          >
            + Add Input & Output Pair
          </Button>
        )}

        <Typography className={classes.sectionTitle}>Efficiency</Typography>
        <Grid container spacing={2} className={classes.sectionBlock}>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" size="small" disabled={fieldDisabled}>
              <InputLabel>What is the efficiency gained by using this agent?</InputLabel>
              <Select
                value={efficiency}
                onChange={e => setEfficiency(String(e.target.value))}
                label="What is the efficiency gained by using this agent?"
              >
                <MenuItem value="">Select efficiency...</MenuItem>
                {efficiencyOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2} className={classes.sectionBlock}>
          <Grid item xs={12}>
            <Box className={classes.validationBox}>
              <FormControlLabel
                className={classes.checkboxLabel}
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={e => setAcknowledged(e.target.checked)}
                    disabled={fieldDisabled}
                    color="primary"
                  />
                }
                label="I acknowledge that AI has to be used responsibly and this agent conforms with all CBRE guidelines for AI Usage. Furthermore, this agent will be available for all of CBRE to see and potentially use, so ensure that the agent does not have sensitive or restricted data."
              />
            </Box>
          </Grid>
        </Grid>

        {isReviewMode ? renderReviewActions() : renderCreateBottomActions()}
      </Box>
    </Box>
  );
};
